import {
  altitudeText,
  speedText,
  verticalText,
  headingText,
  agoText,
  prettyCallsign,
} from "../lib/format.js";
import { isEmergency } from "./MapView.jsx";
import AircraftPhoto from "./AircraftPhoto.jsx";
import AirportWeather from "./AirportWeather.jsx";
import TypeInfo from "./TypeInfo.jsx";
import CompassRose from "./CompassRose.jsx";
import FlightProgress from "./FlightProgress.jsx";
import SpeakButton from "./SpeakButton.jsx";
import AirlineBadge from "./AirlineBadge.jsx";
import SquawkInfo from "./SquawkInfo.jsx";

const EMERGENCY_MEANING = {
  "7500": "Hijack",
  "7600": "Radio failure",
  "7700": "General emergency",
};

function Row({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <div className="row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function AirportLine({ label, airport }) {
  if (!airport) return null;
  const name = airport.name || airport.icao || "Unknown";
  const place = [airport.city, airport.country].filter(Boolean).join(", ");
  return (
    <Row
      label={label}
      value={
        <>
          {name}
          {airport.iata ? ` (${airport.iata})` : ""}
          {place ? <span className="muted"> — {place}</span> : null}
        </>
      }
    />
  );
}

function routeHasCoords(route) {
  const ok = (p) => p && Number.isFinite(p.latitude) && Number.isFinite(p.longitude);
  return !!route && ok(route.origin) && ok(route.destination);
}

export default function DetailsPanel({ aircraft, details, loading, watched, onToggleWatch, onPreview, onOpenCockpit, onClose }) {
  if (!aircraft) return null;
  const hasCallsign = !!(aircraft.callsign && aircraft.callsign.trim());

  const info = details?.aircraft;
  const route = details?.route;
  const title = prettyCallsign(aircraft.callsign);
  const canPreview = !!onPreview && routeHasCoords(route);

  return (
    <aside className="details" aria-label={`Details for ${title}`}>
      <div className="details-head">
        <div>
          <h2>{title}</h2>
          {info?.model || info?.manufacturer ? (
            <p className="subtitle">
              {[info?.manufacturer, info?.model].filter(Boolean).join(" ")}
            </p>
          ) : (
            <p className="subtitle muted">Aircraft {aircraft.icao24.toUpperCase()}</p>
          )}
          <AirlineBadge callsign={aircraft.callsign} />
        </div>
        <button className="close" onClick={onClose} aria-label="Close details">
          ✕
        </button>
      </div>

      {isEmergency(aircraft) ? (
        <p className="alert" role="alert">
          ⚠️ Emergency signal — {EMERGENCY_MEANING[String(aircraft.squawk).trim()] || "squawk " + aircraft.squawk}
        </p>
      ) : null}

      {onOpenCockpit && (
        <button className="cockpit-open" onClick={onOpenCockpit}>
          🛩️ Open cockpit view
        </button>
      )}

      <AircraftPhoto icao={aircraft.icao24} />

      <TypeInfo
        typeCode={info?.typeCode}
        model={info?.model}
        manufacturer={info?.manufacturer}
      />

      {route?.origin || route?.destination ? (
        <div className="route">
          <AirportLine label="From" airport={route.origin} />
          <AirportLine label="To" airport={route.destination} />
          {route.origin?.icao ? (
            <AirportWeather icao={route.origin.icao} label="Weather at origin" />
          ) : null}
          {route.destination?.icao ? (
            <AirportWeather icao={route.destination.icao} label="Weather at destination" />
          ) : null}
        </div>
      ) : null}

      <FlightProgress aircraft={aircraft} route={route} />

      <dl className="rows">
        <Row label="Status" value={altitudeText(aircraft.baroAltitude, aircraft.onGround)} />
        <Row label="Speed" value={speedText(aircraft.velocity)} />
        <Row label="Direction" value={headingText(aircraft.heading)} />
        <Row label="Vertical" value={verticalText(aircraft.verticalRate)} />
        <Row label="Registration" value={info?.registration} />
        <Row label="Operator" value={info?.owner} />
        <Row label="Type code" value={info?.typeCode} />
        <Row label="Registered in" value={aircraft.originCountry || info?.country} />
        <Row label="Transponder (squawk)" value={aircraft.squawk} />
        <Row label="ICAO24 address" value={aircraft.icao24.toUpperCase()} />
        <Row label="Last signal" value={agoText(aircraft.lastContact)} />
      </dl>

      <SquawkInfo squawk={aircraft.squawk} />

      <CompassRose heading={aircraft.heading} />

      <SpeakButton aircraft={aircraft} details={details} />

      {canPreview && (
        <button
          className="preview-flight"
          onClick={() => onPreview(route)}
          title="Fly this route in a 3D cockpit view with predicted sky and weather"
        >
          🛩️ Preview this flight in 3D
        </button>
      )}

      {hasCallsign && (
        <button
          className={watched ? "watch watching" : "watch"}
          onClick={onToggleWatch}
          aria-pressed={!!watched}
        >
          {watched ? "🔔 Watching this flight — tap to stop" : "🔔 Notify me about this flight"}
        </button>
      )}

      {loading ? <p className="muted small">Looking up more details…</p> : null}
      <p className="muted small">
        Data: OpenSky / adsb.lol (positions) · hexdb.io (aircraft &amp; route)
      </p>
    </aside>
  );
}
